import { useEffect, useRef } from 'react';

const areas = [
  {
    name: 'Baytown County',
    state: 'TX',
    icon: 'ri-map-pin-2-line',
    detail: 'Baytown & surrounding communities',
  },
  {
    name: 'East Harris County',
    state: 'TX',
    icon: 'ri-map-pin-2-line',
    detail: 'East Harris County communities',
  },
  {
    name: 'Liberty County',
    state: 'TX',
    icon: 'ri-map-pin-2-line',
    detail: 'Liberty County & surrounding areas',
  },
  {
    name: 'Chambers County',
    state: 'TX',
    icon: 'ri-map-pin-2-line',
    detail: 'Chambers County communities',
  },
];

export default function ServiceAreas() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.area-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    cards.forEach((card, i) => {
      (card as HTMLElement).style.opacity = '0';
      (card as HTMLElement).style.transform = 'translateY(18px)';
      (card as HTMLElement).style.transition = `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`;
      observer.observe(card);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="service-areas" ref={sectionRef} className="py-24 md:py-32 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-14">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]"></div>
              <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.2em]">Where We Serve</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our Service Areas
            </h2>
          </div>
          <p className="text-[#555] text-base leading-relaxed max-w-sm lg:text-right">
            Proudly serving the greater Houston area and surrounding Texas counties with certified, professional inspections.
          </p>
        </div>

        {/* Area Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {areas.map((area) => (
            <div
              key={area.name}
              className="area-card bg-white rounded-lg p-7 border border-[#E8E4DC] hover:border-[#C9A961]/50 transition-all duration-200 group cursor-default"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A961]/10 mb-5 group-hover:bg-[#C9A961]/20 transition-colors duration-200">
                <i className="ri-map-pin-2-line text-[#C9A961] text-lg"></i>
              </div>
              <div className="flex items-baseline gap-2 mb-1.5">
                <h3
                  className="text-lg font-bold text-[#1A1A1A]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {area.name}
                </h3>
                <span className="text-xs font-semibold text-[#C9A961] bg-[#C9A961]/10 px-2 py-0.5 rounded-full">{area.state}</span>
              </div>
              <p className="text-sm text-[#777] leading-relaxed">{area.detail}</p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-5 pt-8 border-t border-[#E8E4DC]">
          <div className="flex items-center gap-3 text-sm text-[#555]">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-information-line text-[#C9A961]"></i>
            </div>
            Not sure if we cover your area? Give us a call — we&apos;ll let you know right away.
          </div>
          <a
            href="tel:+12814217355"
            className="flex items-center gap-2 bg-[#1A1A1A] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#C9A961] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-sm"></i>
            </div>
            Call (281) 421-7355
          </a>
        </div>
      </div>
    </section>
  );
}
